import { useState, useEffect } from 'react';
import { FavoriteItem } from '../../hooks/useFavorites';

interface FavoritesPanelProps {
  isOpen: boolean;
  onClose: () => void;
  favorites: FavoriteItem[];
  onRemove: (id: string) => void;
  onClear: () => void;
  onUpdateQuantity: (id: string, quantity: number) => void;
}

export default function FavoritesPanel({ isOpen, onClose, favorites, onRemove, onClear, onUpdateQuantity }: FavoritesPanelProps) {
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showHowToOrder, setShowHowToOrder] = useState(false);
  
  // Bloquer le scroll de la page quand le panneau est ouvert
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setShowClearConfirm(false);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'pizza-tomate':
      case 'pizza-creme':
      case 'pizza-gourmet':
      case 'pizza-special':
        return 'ri-restaurant-2-line text-terracotta';
      case 'burger':
        return 'ri-hamburger-line text-terracotta';
      case 'dessert':
        return 'ri-cake-2-line text-terracotta';
      case 'frites':
        return 'ri-restaurant-2-line text-terracotta';
      case 'enfants':
        return 'ri-gift-line text-terracotta';
      default:
        return 'ri-restaurant-line text-terracotta';
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'pizza-tomate': return 'Pizza Base Tomate';
      case 'pizza-creme': return 'Pizza Base Crème';
      case 'pizza-gourmet': return 'Pizza Gourmet';
      case 'pizza-special': return 'Pizza Spéciale';
      case 'burger': return 'Burger';
      case 'dessert': return 'Dessert';
      case 'frites': return 'Frites';
      case 'enfants': return 'Menu Enfant';
      default: return 'Autre';
    }
  };

  const getSizeLabel = (size?: 'normal' | 'grande') => {
    if (!size) return '';
    return size === 'normal' ? '(29cm)' : '(40cm)';
  };

  // Ne rien afficher si le panneau n'est pas ouvert
  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-dark/40 backdrop-blur-sm z-40 transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Modal confirmation vider */}
      {showClearConfirm && (
        <div 
          className="fixed inset-0 bg-dark/70 backdrop-blur-sm z-[80] flex items-center justify-center p-4"
          onClick={() => setShowClearConfirm(false)}
        >
          <div 
            className="bg-cream rounded-2xl w-full max-w-xs overflow-hidden shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="bg-terracotta p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
                  <i className="ri-delete-bin-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-base font-bold text-white">Vider les favoris</h3>
                  <p className="text-white/70 text-xs">Action irréversible</p>
                </div>
              </div>
            </div>
            <div className="p-5">
              <p className="text-chocolate text-sm leading-relaxed mb-4">
                Êtes-vous sûr de vouloir supprimer <strong>tous vos favoris</strong> ? Cette action ne peut pas être annulée.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowClearConfirm(false)}
                  className="flex-1 py-3 bg-rose-soft text-chocolate rounded-xl font-semibold text-sm hover:bg-rose-soft/80 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Annuler
                </button>
                <button
                  onClick={() => {
                    onClear();
                    setShowClearConfirm(false);
                  }}
                  className="flex-1 py-3 bg-terracotta text-white rounded-xl font-bold text-sm hover:shadow-lg hover:shadow-terracotta/30 transition-all cursor-pointer whitespace-nowrap"
                >
                  Oui, vider
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Panel */}
      <div className="fixed top-0 right-0 h-full w-full max-w-sm bg-cream z-50 shadow-2xl overflow-hidden">
        <div className="flex flex-col h-full overflow-hidden">
          {/* Header */}
          <div className="relative overflow-hidden flex-shrink-0">
            <div className="absolute inset-0 bg-terracotta"></div>
            <div className="relative p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-lg">
                    <i className="ri-heart-3-fill text-lg text-white"></i>
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white font-display">Mes Favoris</h2>
                    <p className="text-white/70 text-xs">Vos pizzas &amp; burgers favoris</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={onClose}
                    className="w-8 h-8 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-base text-white"></i>
                  </button>
                </div>
              </div>

              {/* Bloc compact : info commande */}
              <div className="bg-white/15 backdrop-blur-sm rounded-xl p-3 border border-white/20">
                <div className="flex items-center gap-1.5">
                  <i className="ri-phone-line text-white/80 text-xs shrink-0"></i>
                  <p className="text-white/80 text-xs leading-relaxed">
                    Commandes <strong>uniquement par téléphone</strong> au <strong className="whitespace-nowrap">03 60 53 27 92</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Comment passer commande — version compacte collapsible */}
          <div className="mx-3 mb-2 flex-shrink-0">
            <button
              onClick={() => setShowHowToOrder(!showHowToOrder)}
              className="w-full flex items-center justify-between px-3 py-2 bg-terracotta/15 hover:bg-terracotta/20 rounded-xl transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center bg-terracotta/20 rounded-lg">
                  <i className="ri-phone-line text-xs text-terracotta"></i>
                </div>
                <span className="text-xs font-semibold text-chocolate">Comment passer commande ?</span>
              </div>
              <i className={`ri-arrow-down-s-line text-terracotta text-base transition-transform ${showHowToOrder ? 'rotate-180' : ''}`}></i>
            </button>

            {showHowToOrder && (
              <div className="mt-1 bg-gradient-to-br from-terracotta to-terracotta/90 rounded-xl p-3 shadow-lg">
                <div className="bg-white/15 backdrop-blur-sm rounded-lg p-2.5">
                  <p className="text-xs text-white/80 mb-2 font-medium">
                    <i className="ri-checkbox-circle-line mr-0.5"></i>
                    Informations à donner lors de l&apos;appel :
                  </p>
                  <div className="grid grid-cols-2 gap-1.5">
                    <div className="flex items-center gap-1.5 bg-white/10 rounded px-2 py-1.5">
                      <div className="w-5 h-5 flex items-center justify-center bg-white/20 rounded">
                        <i className="ri-user-line text-xs text-white"></i>
                      </div>
                      <span className="text-xs text-white">Votre <strong>nom</strong></span>
                    </div>
                    <div className="flex items-center gap-1.5 bg-white/10 rounded px-2 py-1.5">
                      <div className="w-5 h-5 flex items-center justify-center bg-white/20 rounded">
                        <i className="ri-restaurant-line text-xs text-white"></i>
                      </div>
                      <span className="text-xs text-white">Vos <strong>souhaits</strong></span>
                    </div>
                    <div className="flex items-center gap-1.5 bg-white/10 rounded px-2 py-1.5">
                      <div className="w-5 h-5 flex items-center justify-center bg-white/20 rounded">
                        <i className="ri-shopping-bag-line text-xs text-white"></i>
                      </div>
                      <span className="text-xs text-white"><strong>Emporter</strong> / <strong>Livraison (+4€)</strong></span>
                    </div>
                    <div className="flex items-center gap-1.5 bg-white/10 rounded px-2 py-1.5">
                      <div className="w-5 h-5 flex items-center justify-center bg-white/20 rounded">
                        <i className="ri-map-pin-line text-xs text-white"></i>
                      </div>
                      <span className="text-xs text-white"><strong>Adresse</strong> (livraison)</span>
                    </div>
                    <div className="flex items-center gap-1.5 bg-white/10 rounded px-2 py-1.5 col-span-2">
                      <div className="w-5 h-5 flex items-center justify-center bg-white/20 rounded">
                        <i className="ri-time-line text-xs text-white"></i>
                      </div>
                      <span className="text-xs text-white"><strong>Heure</strong> souhaitée</span>
                    </div>
                  </div>
                  <div className="mt-2 pt-2 border-t border-white/20">
                    <p className="text-[10px] text-white/60 text-center">
                      <i className="ri-truck-line mr-0.5"></i>
                      Livraison +4€ (min. 2 pizzas) • Jeu/Ven/Sam uniquement
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Content - Zone scrollable */}
          <div className="flex-1 overflow-y-auto px-3 pb-3 overscroll-contain">
            {favorites.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-6">
                <div className="w-16 h-16 flex items-center justify-center bg-rose-soft rounded-xl mb-3">
                  <i className="ri-heart-line text-3xl text-terracotta/30"></i>
                </div>
                <h3 className="text-base font-semibold text-chocolate mb-1 font-display">Aucun favori</h3>
                <p className="text-dark/50 text-sm max-w-xs mb-4">
                  Cliquez sur le cœur à côté d&apos;une pizza ou d&apos;un burger pour l&apos;ajouter à vos favoris
                </p>
                <button
                  onClick={() => {
                    onClose();
                    document.getElementById('menu')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="flex items-center gap-2 px-5 py-2.5 bg-terracotta text-white rounded-xl text-sm font-semibold hover:shadow-lg hover:shadow-terracotta/30 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-restaurant-line"></i>
                  Voir le menu
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {favorites.map((item) => {
                  const qty = item.quantity || 1;
                  return (
                    <div 
                      key={item.id}
                      className="bg-rose-soft rounded-xl p-3 hover:shadow-sm transition-all duration-300"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <div className="w-5 h-5 flex items-center justify-center bg-cream rounded flex-shrink-0">
                              <i className={`${getCategoryIcon(item.category)} text-xs`}></i>
                            </div>
                            <span className="text-xs text-dark/50 font-medium truncate">
                              {getCategoryLabel(item.category)}
                            </span>
                          </div>
                          <h4 className="font-bold text-chocolate text-sm leading-tight">
                            {qty > 1 && <span className="text-terracotta">{qty}x </span>}
                            {item.name} {getSizeLabel(item.size)}
                          </h4>
                          <p className="text-dark/50 text-xs mt-0.5 line-clamp-2">{item.ingredients}</p>
                          
                          {/* Suppléments */}
                          {item.supplements && item.supplements.length > 0 && (
                            <div className="mt-1.5">
                              <div className="flex flex-wrap gap-1">
                                {item.supplements.map((s, idx) => (
                                  <span key={idx} className="text-xs bg-terracotta/15 text-terracotta px-1.5 py-0.5 rounded-full">
                                    +{s.name} ({s.price}€)
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Ingrédients retirés */}
                          {item.removedIngredients && item.removedIngredients.length > 0 && (
                            <div className="mt-1.5">
                              <div className="flex flex-wrap gap-1">
                                {item.removedIngredients.map((ing, idx) => (
                                  <span key={idx} className="text-xs bg-chocolate/15 text-chocolate px-1.5 py-0.5 rounded-full">
                                    Sans {ing}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Note burger */}
                          {item.note && (
                            <div className="mt-1.5 bg-chocolate/10 rounded-lg px-2 py-1">
                              <p className="text-xs text-chocolate italic">
                                <i className="ri-edit-line mr-1 text-[10px]"></i>
                                {item.note}
                              </p>
                            </div>
                          )}
                          
                          {/* Quantité + Prix */}
                          <div className="flex items-center justify-between mt-2 pt-2 border-t border-cream">
                            {/* Sélecteur de quantité */}
                            <div className="flex items-center gap-0.5">
                              <button
                                onClick={() => {
                                  if (qty <= 1) {
                                    onRemove(item.id);
                                  } else {
                                    onUpdateQuantity(item.id, qty - 1);
                                  }
                                }}
                                className={`w-7 h-7 flex items-center justify-center rounded-lg transition-all cursor-pointer ${
                                  qty <= 1 
                                    ? 'bg-chocolate/10 text-chocolate/50 hover:bg-red-100 hover:text-red-500' 
                                    : 'bg-terracotta/15 text-terracotta hover:bg-terracotta/25'
                                }`}
                              >
                                <i className={`${qty <= 1 ? 'ri-delete-bin-line' : 'ri-subtract-line'} text-sm`}></i>
                              </button>
                              <span className="w-8 text-center text-sm font-bold text-chocolate">{qty}</span>
                              <button
                                onClick={() => onUpdateQuantity(item.id, qty + 1)}
                                className="w-7 h-7 flex items-center justify-center bg-terracotta/15 text-terracotta rounded-lg hover:bg-terracotta/25 transition-all cursor-pointer"
                              >
                                <i className="ri-add-line text-sm"></i>
                              </button>
                            </div>
                            
                            {/* Prix */}
                            <div className="text-right">
                              <p className="text-terracotta font-bold text-sm">{item.price}</p>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => onRemove(item.id)}
                          className="w-7 h-7 flex items-center justify-center text-dark/40 hover:text-white hover:bg-terracotta rounded-lg transition-all cursor-pointer flex-shrink-0"
                        >
                          <i className="ri-close-line text-sm"></i>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {favorites.length > 0 && (
            <div className="border-t border-rose-soft p-3 bg-cream flex-shrink-0">
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => setShowClearConfirm(true)}
                  className="px-3 py-2.5 bg-rose-soft text-dark/60 rounded-xl text-xs font-medium hover:bg-rose-soft/80 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-delete-bin-line mr-1"></i>
                  Vider
                </button>
                <a
                  href="tel:0360532792"
                  className="flex-1 group flex items-center justify-center gap-2 bg-terracotta text-white py-2.5 rounded-xl font-bold text-sm hover:shadow-xl hover:shadow-terracotta/30 transition-all cursor-pointer whitespace-nowrap"
                >
                  <span className="w-7 h-7 flex items-center justify-center bg-white/20 rounded group-hover:scale-110 transition-transform">
                    <i className="ri-phone-fill text-sm"></i>
                  </span>
                  Appeler Pizz&apos;Ajo
                </a>
              </div>

              <div className="bg-rose-soft/50 rounded-lg p-2 text-center">
                <p className="text-dark/60 text-[10px] leading-relaxed">
                  <i className="ri-bookmark-line mr-0.5"></i>
                  Ceci est un aide-mémoire pour préparer votre appel
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}